"use client";

import type { FeatureTryAction } from "@gettactic/defines/src/feature-announcements";
import React, { createContext, useCallback, useContext } from "react";

import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";

export interface FeatureActionHandlers {
  onRedirect?: (url: string) => void;
  onOpenGlobalSearch?: () => void;
}

interface FeatureActionContextType {
  executeAction: (action: FeatureTryAction) => void;
}

const FeatureActionContext = createContext<FeatureActionContextType | null>(
  null
);

interface FeatureActionProviderProps {
  children: React.ReactNode;
  handlers?: FeatureActionHandlers;
}

export const FeatureActionProvider: React.FC<FeatureActionProviderProps> = ({
  children,
  handlers
}) => {
  const askTessa = useAskTessa();

  const executeAction = useCallback(
    (action: FeatureTryAction) => {
      switch (action.type) {
        case "open-tessa":
          askTessa?.open();
          break;

        case "open-global-search":
          handlers?.onOpenGlobalSearch?.();
          break;

        case "redirect":
          if (action.url) {
            handlers?.onRedirect?.(action.url);
          }
          break;

        case "external-url":
          if (action.url) {
            window.open(action.url, "_blank", "noopener,noreferrer");
          }
          break;
      }
    },
    [askTessa, handlers]
  );

  return (
    <FeatureActionContext.Provider value={{ executeAction }}>
      {children}
    </FeatureActionContext.Provider>
  );
};

export const useFeatureAction = (): FeatureActionContextType => {
  const context = useContext(FeatureActionContext);
  if (!context) {
    // Return a no-op if used outside provider
    return {
      executeAction: () => {}
    };
  }
  return context;
};
